import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Grid,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as DuplicateIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ViewModule as ComponentIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ComponentDefinition, CreateComponentRequest } from '../types/components';
import { ComponentService } from '../services/componentService';
import ComponentBuilder from '../components/components/ComponentBuilder';
import { Logger } from '../utils/logger';

const ComponentBuilderPage: React.FC = () => {
  const [view, setView] = useState<'list' | 'create' | 'edit'>('list');
  const [selectedComponent, setSelectedComponent] = useState<ComponentDefinition | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [componentToDelete, setComponentToDelete] = useState<ComponentDefinition | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');

  const queryClient = useQueryClient();

  // Query for components list
  const { 
    data: components = [], 
    isLoading, 
    error, 
    refetch 
  } = useQuery({
    queryKey: ['components', { 
      search: searchTerm,
      category: categoryFilter 
    }],
    queryFn: () => ComponentService.getComponents({
      search: searchTerm || undefined,
      category: categoryFilter || undefined
    }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Create component mutation
  const createComponentMutation = useMutation({
    mutationFn: (componentData: CreateComponentRequest) => ComponentService.createComponent(componentData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['components'] });
      setView('list');
      setSelectedComponent(null);
    },
    onError: (error) => {
      Logger.error('Failed to create component', error as Error, {
        service: 'ComponentBuilderPage',
        operation: 'createComponent'
      });
    }
  });

  // Update component mutation
  const updateComponentMutation = useMutation({
    mutationFn: (componentData: CreateComponentRequest) => {
      if (!selectedComponent) throw new Error('No component selected');
      return ComponentService.updateComponent({ ...componentData, id: selectedComponent.id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['components'] });
      setView('list');
      setSelectedComponent(null);
    },
    onError: (error) => {
      Logger.error('Failed to update component', error as Error, {
        service: 'ComponentBuilderPage',
        operation: 'updateComponent'
      });
    }
  });

  // Delete component mutation
  const deleteComponentMutation = useMutation({
    mutationFn: (id: string) => ComponentService.deleteComponent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['components'] });
      setDeleteDialogOpen(false);
      setComponentToDelete(null);
    },
    onError: (error) => {
      Logger.error('Failed to delete component', error as Error, {
        service: 'ComponentBuilderPage',
        operation: 'deleteComponent'
      });
    }
  });

  // Duplicate component mutation
  const duplicateComponentMutation = useMutation({
    mutationFn: ({ id, newName }: { id: string; newName?: string }) => 
      ComponentService.duplicateComponent(id, newName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['components'] });
    },
    onError: (error) => {
      Logger.error('Failed to duplicate component', error as Error, {
        service: 'ComponentBuilderPage',
        operation: 'duplicateComponent'
      });
    }
  });

  // Filter components based on search term and category
  const filteredComponents = components.filter(component => {
    const matchesSearch = !searchTerm || 
      component.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      component.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      component.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = !categoryFilter || component.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  const handleCreateComponent = async (componentData: CreateComponentRequest) => {
    await createComponentMutation.mutateAsync(componentData);
  };

  const handleEditComponent = (component: ComponentDefinition) => {
    setSelectedComponent(component);
    setView('edit');
  };

  const handleUpdateComponent = async (componentData: CreateComponentRequest) => {
    await updateComponentMutation.mutateAsync(componentData);
  };

  const handleDeleteComponent = (component: ComponentDefinition) => {
    setComponentToDelete(component);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteComponent = async () => {
    if (componentToDelete) {
      await deleteComponentMutation.mutateAsync(componentToDelete.id);
    }
  };

  const handleDuplicateComponent = async (component: ComponentDefinition) => {
    const newName = `${component.name}_copy`;
    await duplicateComponentMutation.mutateAsync({ 
      id: component.id, 
      newName 
    });
  };

  const handleCancelEdit = () => {
    setView('list');
    setSelectedComponent(null);
  };

  // Clear search and filters
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Trigger refetch when search term changes
      refetch();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, categoryFilter, refetch]);

  if (view === 'create' || view === 'edit') {
    return (
      <ComponentBuilder
        component={selectedComponent}
        onSave={view === 'create' ? handleCreateComponent : handleUpdateComponent}
        onCancel={handleCancelEdit}
      />
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ComponentIcon sx={{ fontSize: 32 }} />
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Component Builder
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setView('create')}
          size="large"
          sx={{ borderRadius: 2 }}
        >
          Create Component
        </Button>
      </Box>

      {/* Description */}
      <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f8f9fa' }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Build Custom Components
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Create reusable components with custom properties, styling, and templates. 
          Components can be used in pages and forms throughout the platform.
        </Typography>
      </Paper>

      {/* Filters and Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              placeholder="Search components..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Category"
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="">All Categories</MenuItem>
                <MenuItem value="Content">Content</MenuItem>
                <MenuItem value="Layout">Layout</MenuItem>
                <MenuItem value="Data">Data</MenuItem>
                <MenuItem value="Interactive">Interactive</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 2, md: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
              size="small"
            >
              Refresh
            </Button>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Chip 
                label={`${filteredComponents.length} component${filteredComponents.length !== 1 ? 's' : ''}`}
                color="primary"
                variant="outlined"
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load components: {error.message}
        </Alert>
      )}

      {/* Components Table */}
      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Properties</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary">Loading components...</Typography>
                  </TableCell>
                </TableRow>
              ) : filteredComponents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Box sx={{ py: 4 }}>
                      <ComponentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        {searchTerm || categoryFilter ? 'No components match your search' : 'No components created yet'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {searchTerm || categoryFilter 
                          ? 'Try adjusting your search criteria'
                          : 'Create your first custom component to get started'
                        }
                      </Typography>
                      {!searchTerm && !categoryFilter && (
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => setView('create')}
                        >
                          Create First Component
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredComponents.map((component) => (
                  <TableRow key={component.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {component.displayName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {component.name}
                        </Typography>
                        {component.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {component.description}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={component.category}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {component.props.length} {component.props.length === 1 ? 'property' : 'properties'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={component.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={component.isActive ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(component.updatedAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditComponent(component)}
                          title="Edit component"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDuplicateComponent(component)}
                          title="Duplicate component"
                          disabled={duplicateComponentMutation.isPending}
                        >
                          <DuplicateIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteComponent(component)}
                          color="error"
                          title="Delete component"
                          disabled={component.isSystem}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Component</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the component "{componentToDelete?.displayName}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. Any pages using this component may be affected.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteComponent}
            color="error"
            variant="contained"
            disabled={deleteComponentMutation.isPending}
          >
            {deleteComponentMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ComponentBuilderPage;